import styled from 'styled-components'
import { Card, Col } from 'react-bootstrap'
import { ChartOptions, Scale, CoreScaleOptions, Tick } from 'chart.js'

export const StepCard = styled(Card)`
  background: #2a2a2a;
  border: none;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  .card-title {
    font-size: 1.25rem;
    margin-bottom: 0;
    color: #ffffff;
  }

  .text-muted {
    font-size: 0.9rem;
    opacity: 0.7;
    color: #ffffff;
  }

  .gauge-metric {
    background-color: rgba(0, 0, 0, 0.2);
    padding: 0.75rem;
    border-radius: 8px;
    text-align: center;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    h5 {
      color: #ffffff;
      margin-bottom: 1rem;
      font-size: 0.9rem;
      flex-shrink: 0;
      width: 100%;
      text-align: left;
    }

    svg {
      flex: 1;
      width: 100% !important;
      height: auto !important;
      min-height: 0;
      margin-top: auto;
    }

    .metric-value {
      margin-bottom: 0;
      font-size: 0.9rem;
    }
  }
`

export const StepHeader = styled.div<{ $gradient: string }>`
  background: ${(props) => props.$gradient};
  padding: 1rem;
  border-radius: 8px 8px 0 0;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`

export const StepContent = styled.div`
  background: #2a2a2a;
  padding: 1.5rem;
  border-radius: 0 0 8px 8px;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-height: 0;

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #1a1a1a;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: #4a4a4a;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #5a5a5a;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`

export const StatusCard = styled.div`
  background: #1a1a1a;
  border-radius: 6px;
  padding: 1rem;
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  span {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.85rem;
    margin-bottom: 0.25rem;
  }

  strong {
    color: #4dabf7;
    font-size: 1rem;
    display: block;
  }
`

export const SourceGroup = styled.div`
  background: #1a1a1a;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;

  .source-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }

  .dataset-container {
    color: #ffffff;
  }
`

export const GaugeCardContainer = styled.div<{ $color?: string }>`
  perspective: 1000px;
  height: 100%;
  width: 100%;
  transform-style: preserve-3d;
`

export const GaugeCardInner = styled.div<{ $isFlipped: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  transform: ${(props) => (props.$isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)')};
`

export const CardSide = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
`

export const GaugeCardFront = styled(CardSide)<{ $color?: string }>`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  border: 1px solid transparent;
  transition: all 0.2s ease-in-out;
  transform: rotateY(0deg);

  &:hover {
    border-color: ${(props) => props.$color || 'transparent'};
    box-shadow: 0 0 15px 0 ${(props) => (props.$color ? `${props.$color}40` : 'transparent')};
  }

  .gauge-container {
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;

    svg {
      width: 100% !important;
      height: 100% !important;
      max-width: 300px;
      max-height: 300px;
    }
  }
`

export const GaugeCardBack = styled(CardSide)<{ $color?: string }>`
  transform: rotateY(180deg);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  border: 1px solid ${(props) => props.$color || 'transparent'};
  color: rgba(255, 255, 255, 0.9);
`

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  h5 {
    color: rgba(255, 255, 255, 0.9);
    font-size: 1rem;
    font-weight: 600;
    margin: 0;
    text-align: left;
  }

  button {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    padding: 0.25rem;
    cursor: pointer;
    transition: color 0.2s ease-in-out;

    &:hover {
      color: rgba(255, 255, 255, 0.9);
    }
  }
`

export const BackContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  p {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.5rem;
    flex: 1;
    text-align: left;
    line-height: 1.5;
  }
`

export const MetricsCardContainer = styled.div`
  perspective: 1000px;
  height: 100%;

  .metrics-step-content {
    overflow: hidden;
    padding: 0.75rem 1.5rem 1.5rem 1.5rem;
    border-radius: 0 0 8px 8px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }
`

export const MetricsCardInner = styled.div<{ $isFlipped: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  transform: ${(props) => (props.$isFlipped ? 'rotateY(180deg)' : 'rotateY(0)')};
`

export const MetricsCardSide = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  background: #2a2a2a;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`

export const MetricsCardFront = styled(MetricsCardSide)``

export const MetricsCardBack = styled(MetricsCardSide)`
  transform: rotateY(180deg);
`

export const MetricsChartContainer = styled.div`
  flex: 1;
  padding: 0 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 2.2rem;
  // min-height: 0;
  overflow-y: auto;
  height: 100%;
  ${StepContent} {
    padding-top: 0.15rem;
  }

  .chart-section {
    flex: 1;
    // min-height: 250px;

    h6 {
      color: rgba(255, 255, 255, 0.9);
      font-size: 0.9rem;
      font-weight: 600;
      margin-bottom: 0.1rem;
    }
  }
`

export const GaugeColumn = styled(Col)`
  height: 265px;
  margin-bottom: ${(props) => (props.index < 2 ? '1rem' : '0')};

  @media (max-width: 991px) {
    height: 310px;
  }

  .gauge-metric {
    background-color: rgba(0, 0, 0, 0.2);
    padding: 0.75rem;
    border-radius: 8px;
    text-align: center;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    h5 {
      color: #ffffff;
      margin-bottom: 0.75rem;
      font-size: 0.9rem;
      flex-shrink: 0;
      width: 100%;
      text-align: left;
    }
  }
`

export const metricsChartOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        color: '#ffffff',
      },
    },
    tooltip: {
      callbacks: {
        label: function (context: any) {
          const label = context.dataset.label || ''
          const value = context.raw
          const displayValue =
            context.dataset.originalData?.[context.dataIndex] === null
              ? 'null'
              : (value || 0).toFixed(4)
          return `${label}: ${displayValue}`
        },
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: {
        display: false,
      },
      ticks: {
        color: '#ffffff',
        autoSkip: true,
        callback: function (
          this: Scale<CoreScaleOptions>,
          tickValue: number | string,
          index: number,
          ticks: Tick[]
        ): string | number | null {
          if (index === 0 || index === ticks.length - 1) {
            return this.getLabelForValue(tickValue as number)
          }
          if (index % Math.ceil(ticks.length / 10) === 0) {
            return this.getLabelForValue(tickValue as number)
          }
          return null
        },
        maxRotation: 45,
        minRotation: 45,
        includeBounds: true,
      },
      afterFit: (scale: any) => {
        scale.paddingRight = 20
      },
    },
    y: {
      type: 'linear' as const,
      beginAtZero: true,
      grid: {
        color: 'rgba(255, 255, 255, 0.1)',
      },
      ticks: {
        color: '#ffffff',
      },
    },
  },
}

export const lossChartOptions: ChartOptions<'line'> = {
  ...metricsChartOptions,
  scales: {
    ...(metricsChartOptions.scales || {}),
    y: {
      ...(metricsChartOptions.scales?.y || {}),
    },
  },
}
